<template>
  <div class="app-main-content provision-rule-tab">
    <div class="grid-view">
      <div class="main-content">
        <search-item
          ref="searchItem"
          config-module="25002003"
          :show-simple="false"
          :show-adv="true"
          :expand-adv="true"
          :auto-init="false"
          :custom-search-items="customSearchItems"
          @advSearchItemCallBack="onAdvancedQuery"
          @resetFields="onResetFields"
        >
          <div slot="operations">
            <el-button type="primary" @click="addHandler">新增</el-button>
            <el-button type="primary" @click="editHandler">编辑</el-button>
            <el-button type="danger" @click="deleteHandler">删除</el-button>
          </div>
        </search-item>
        <div class="section grid-wrapper">
          <cargo-ag-grid
            :grid-options="gridOptions"
            :column-defs="columnDefs"
            :row-data="pageRowData"
            style="width: 100%; height: 100%; min-height: 360px;"
            @gridReady="params => { gridApi = params.api }"
          />
        </div>
        <div class="pagination-wrapper">
          <cargo-pagination
            :total="filter.total"
            :page-count="pageCount"
            :page.sync="filter.pageNum"
            :limit.sync="filter.pageSize"
            @pagination="loadData"
          />
        </div>
      </div>
    </div>

    <group-edit-dialog
      v-if="groupDialogVisible"
      :visible.sync="groupDialogVisible"
      :group-uuid="editingGroupUuid"
      :group-name="editingGroupName"
      :group-station="editingGroupStation"
      :initial-members="editingGroupMembers"
      @success="loadData"
    />
  </div>
</template>

<script>
import searchItem from '@/components/Cargo/Common/cargoCombinationSearch'
import GroupEditDialog from '../components/GroupEditDialog.vue'
import { queryPersonGroups, deletePersonGroup } from '@/api/provision/provisionRule'
import * as ProvisionConstants from '@/class/cargo/model/parameters/ProvisionConstants'
import {
  applySearchConditions,
  createSelectSearchField,
  createTextSearchField,
  initLocalSearchItem
} from '../utils/provisionRuleSearch'

export default {
  name: 'PersonGroupTab',
  components: { searchItem, GroupEditDialog },
  data() {
    return {
      configurableStations: ProvisionConstants.CONFIGURABLE_STATIONS,
      customSearchItems: [
        createTextSearchField('组名称', 'groupName'),
        createSelectSearchField('所属岗位', 'stationName', ProvisionConstants.CONFIGURABLE_STATIONS)
      ],
      filter: {
        groupName: '',
        stationName: '',
        pageNum: 1,
        pageSize: 50,
        total: 0
      },
      allRowData: [],
      selectData: null,
      selectedRows: [],
      gridApi: null,
      gridOptions: {
        rowSelection: 'multiple',
        suppressCellSelection: true,
        suppressRowClickSelection: true,
        onRowClicked: (event) => {
          this.selectData = event.data
        },
        onSelectionChanged: (event) => {
          this.selectedRows = event.api.getSelectedRows()
          this.selectData = this.selectedRows[0] || null
        }
      },
      columnDefs: [
        {
          headerName: '',
          width: 55,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          resizable: false
        },
        { headerName: '组名称', field: 'groupName', width: 180 },
        { headerName: '所属岗位', field: 'stationName', width: 120 },
        {
          headerName: '成员',
          flex: 1,
          minWidth: 240,
          valueFormatter: (params) => {
            const members = params.data && params.data.members
            if (!members || !members.length) return '暂无成员'
            return members.map(m => m.userName || m.userId).join('、')
          }
        }
      ],
      groupDialogVisible: false,
      editingGroupUuid: '',
      editingGroupName: '',
      editingGroupStation: '',
      editingGroupMembers: []
    }
  },
  computed: {
    pageRowData() {
      const start = (this.filter.pageNum - 1) * this.filter.pageSize
      return this.allRowData.slice(start, start + this.filter.pageSize)
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filter.total / this.filter.pageSize) || 1)
    }
  },
  methods: {
    init() {
      this.$nextTick(() => initLocalSearchItem(this.$refs.searchItem))
      this.loadData()
    },
    onAdvancedQuery(conditions) {
      applySearchConditions(this.filter, conditions, ['groupName', 'stationName'])
      this.filter.pageNum = 1
      this.loadData()
    },
    onResetFields() {
      applySearchConditions(this.filter, [], ['groupName', 'stationName'])
      this.filter.pageNum = 1
      this.loadData()
    },
    async loadData() {
      try {
        const res = await queryPersonGroups({
          groupName: this.filter.groupName || undefined,
          stationName: this.filter.stationName || undefined
        })
        if (!res.ok) {
          this.$message.error('加载人员组失败')
          return
        }
        this.allRowData = (res.data || []).map(g => ({
          uuid: g.uuid,
          groupName: g.groupName,
          stationName: g.stationName,
          members: g.members || []
        }))
        this.filter.total = this.allRowData.length
        if (this.filter.pageNum > this.pageCount) {
          this.filter.pageNum = 1
        }
        this.selectData = null
        this.selectedRows = []
      } catch (e) {
        console.error('loadData failed', e)
        this.$message.error('加载人员组失败')
      }
    },
    addHandler() {
      this.editingGroupUuid = ''
      this.editingGroupName = ''
      this.editingGroupStation = ''
      this.editingGroupMembers = []
      this.groupDialogVisible = true
    },
    getCheckedRows() {
      if (this.gridApi) {
        return this.gridApi.getSelectedRows()
      }
      return this.selectedRows || []
    },
    requireSingleRow() {
      const rows = this.getCheckedRows()
      if (rows.length !== 1) {
        this.$message.warning(rows.length > 1 ? '只能选择一条记录' : '请先选择一条记录')
        return null
      }
      return rows[0]
    },
    editHandler() {
      const row = this.requireSingleRow()
      if (!row) return
      this.editingGroupUuid = row.uuid
      this.editingGroupName = row.groupName || ''
      this.editingGroupStation = row.stationName || ''
      this.editingGroupMembers = Array.isArray(row.members)
        ? row.members.map(m => ({
          userId: String(m.userId),
          userName: m.userName || m.userId
        }))
        : []
      this.groupDialogVisible = true
    },
    deleteHandler() {
      const rows = this.getCheckedRows()
      if (!rows.length) {
        this.$message.warning('请先选择记录')
        return
      }
      this.$confirm(`确认删除选中的 ${rows.length} 个人员组？`, '提示', { type: 'warning' }).then(async() => {
        await Promise.all(rows.map(row => deletePersonGroup(row.uuid)))
        this.$message.success('已删除')
        this.loadData()
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.grid-wrapper {
  flex-grow: 1;
  min-height: 360px;
}
.pagination-wrapper {
  margin-top: 8px;
  text-align: right;
}
</style>
